import React from 'react'
import axios from 'axios'
import {
  Dialog,
  Typography,
  Checkbox,
  CircularProgress,
  IconButton,
  Chip,
  Button
} from '@material-ui/core'
import {
  Close as CloseIcon
} from '@material-ui/icons'

// Discipline config (matches PlanPageSelector)
const DISCIPLINE_CONFIG = {
  landscape: { color: '#2e7d32', bg: '#e8f5e9', label: 'Landscape' },
  irrigation: { color: '#1565c0', bg: '#e3f2fd', label: 'Irrigation' },
  lighting: { color: '#f57f17', bg: '#fff8e1', label: 'Lighting' },
  hardscape: { color: '#6d4c41', bg: '#efebe9', label: 'Hardscape' },
  grading: { color: '#757575', bg: '#f5f5f5', label: 'Grading' },
  demolition: { color: '#c62828', bg: '#ffebee', label: 'Demolition' },
  cover: { color: '#546e7a', bg: '#eceff1', label: 'Cover Sheet' },
  notes: { color: '#546e7a', bg: '#eceff1', label: 'Notes' },
  detail: { color: '#7b1fa2', bg: '#f3e5f5', label: 'Detail' },
  tree_inventory: { color: '#33691e', bg: '#f1f8e9', label: 'Tree Inventory' },
  other: { color: '#9e9e9e', bg: '#fafafa', label: 'Other' }
}

const getDisciplineConfig = (d) => DISCIPLINE_CONFIG[d] || DISCIPLINE_CONFIG.other

const normalizeCanonicalPages = (pages) => {
  if (!Array.isArray(pages)) return []
  return pages.map((page) => ({
    page: page.pageNumber,
    sheetNumber: page.sheetNumber || '',
    discipline: page.discipline || 'other',
    title: page.title || `Page ${page.pageNumber}`,
    hasSchedule: Boolean(page.hasSchedule)
  }))
}

const VisualReviewModal = ({ open, onClose, sheetIndex, selected, projectId, docId, url, readOnly }) => {
  const [canonicalSheetIndex, setCanonicalSheetIndex] = React.useState([])
  const [activePage, setActivePage] = React.useState(
    () => (sheetIndex && sheetIndex.length > 0) ? sheetIndex[0].page : 1
  )
  const [localSelected, setLocalSelected] = React.useState(() => new Set(selected))
  const [s3UrlMap, setS3UrlMap] = React.useState({}) // { pageNum: presignedUrl }
  const [s3Ready, setS3Ready] = React.useState(false)
  const [pagesLoading, setPagesLoading] = React.useState(false)
  const [imageLoading, setImageLoading] = React.useState(false)
  const [imageError, setImageError] = React.useState('')
  const sidebarRefs = React.useRef({})
  const effectiveSheetIndex = canonicalSheetIndex.length > 0 ? canonicalSheetIndex : (Array.isArray(sheetIndex) ? sheetIndex : [])

  // Load presigned S3 URLs once when modal opens
  React.useEffect(() => {
    if (!open || !projectId || !docId) return
    setActivePage(effectiveSheetIndex && effectiveSheetIndex.length > 0 ? effectiveSheetIndex[0].page : 1)
    setLocalSelected(new Set(selected))
    setS3UrlMap({})
    setS3Ready(false)
    setImageLoading(true)
    setImageError('')

    axios.get(
      `${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/page-images`
    ).then(res => {
      const map = {}
      ;(res.data.pages || []).forEach(p => { if (p.url) map[p.page] = p.url })
      setS3UrlMap(map)
      setS3Ready(true)
      setImageLoading(false)
    }).catch(err => {
      setImageError(err.response?.data?.error || 'Failed to load page images')
      setImageLoading(false)
    })
  }, [open, projectId, docId, selected, url])

  React.useEffect(() => {
    if (!open || !projectId || !docId) return undefined
    let canceled = false
    setPagesLoading(true)
    axios.get(`${url}/api/bid-estimation/projects/${projectId}/documents/${docId}/view`).then(res => {
      if (canceled) return
      const nextPages = normalizeCanonicalPages(res.data && res.data.pages)
      setCanonicalSheetIndex(nextPages)
      setPagesLoading(false)
      if (nextPages.length > 0) {
        setActivePage(prev => (nextPages.some(page => page.page === prev) ? prev : nextPages[0].page))
      }
    }).catch(() => {
      if (canceled) return
      setCanonicalSheetIndex([])
      setPagesLoading(false)
    })
    return () => { canceled = true }
  }, [open, projectId, docId, url])

  // Keyboard navigation — scoped, ignores inputs
  React.useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault()
        setActivePage(prev => {
          const idx = effectiveSheetIndex.findIndex(s => s.page === prev)
          if (idx < 0) return prev
          const next = e.key === 'ArrowLeft' ? idx - 1 : idx + 1
          if (next < 0 || next >= effectiveSheetIndex.length) return prev
          return effectiveSheetIndex[next].page
        })
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, effectiveSheetIndex])

  // Scroll active page into view in sidebar
  React.useEffect(() => {
    const el = sidebarRefs.current[activePage]
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [activePage])

  const togglePage = (page) => {
    setLocalSelected(prev => {
      const next = new Set(prev)
      if (next.has(page)) next.delete(page)
      else next.add(page)
      return next
    })
  }

  const handleClose = () => {
    if (onClose) onClose(localSelected)
  }

  const activeSheet = effectiveSheetIndex.find(s => s.page === activePage)
  const activeCfg = activeSheet ? getDisciplineConfig(activeSheet.discipline) : null
  const activeImageUrl = s3UrlMap[activePage]

  if (!open) return null

  return (
    <Dialog open={open} fullScreen onClose={handleClose}>
      <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>

        {/* Left: Image viewer (edge-to-edge) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#f0f0f0' }}>
          {/* Header bar */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 16px',
            background: '#fff',
            borderBottom: '1px solid #e0e0e0',
            minHeight: 48
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                PDF Page {activePage} of {effectiveSheetIndex.length}
              </Typography>
              {activeSheet && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    — {activeSheet.sheetNumber ? `${activeSheet.sheetNumber} ` : ''}{activeSheet.title}
                  </Typography>
                  <Chip
                    size="small"
                    label={activeCfg.label}
                    style={{
                      backgroundColor: activeCfg.bg,
                      color: activeCfg.color,
                      height: 20,
                      fontSize: 11,
                      fontWeight: 500
                    }}
                  />
                  {activeSheet.hasSchedule && (
                    <Chip
                      size="small"
                      label="Has Schedule"
                      style={{
                        backgroundColor: '#fff8e1',
                        color: '#f57f17',
                        height: 18,
                        fontSize: 10,
                        fontWeight: 600
                      }}
                    />
                  )}
                </>
              )}
            </div>
            {!readOnly && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                <Checkbox
                  checked={localSelected.has(activePage)}
                  onChange={() => togglePage(activePage)}
                  size="small"
                  color="primary"
                  style={{ padding: 2 }}
                />
                <span style={{ fontWeight: 500 }}>Select for extraction</span>
              </label>
            )}
          </div>

          {/* Image area — fills remaining space, no padding */}
          <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
            {(imageLoading || pagesLoading) && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1
              }}>
                <CircularProgress size={40} />
              </div>
            )}

            {imageError && !imageLoading && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#c62828',
                fontSize: 14
              }}>
                {imageError}
              </div>
            )}

            {s3Ready && activeImageUrl && (
              <img
                src={activeImageUrl}
                alt={`Page ${activePage}`}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  display: 'block'
                }}
              />
            )}

            {s3Ready && !activeImageUrl && !imageError && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#999',
                fontSize: 14
              }}>
                No preview available for page {activePage}
              </div>
            )}
          </div>

          {/* Keyboard hint */}
          <div style={{
            padding: '4px 16px',
            fontSize: 11,
            color: '#999',
            background: '#fff',
            borderTop: '1px solid #e0e0e0',
            textAlign: 'right'
          }}>
            ← → arrow keys to navigate
          </div>
        </div>

        {/* Right: Sidebar */}
        <div style={{
          width: 280,
          minWidth: 280,
          borderLeft: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          background: '#fafafa'
        }}>
          {/* Sidebar header */}
          <div style={{ padding: '12px 14px', borderBottom: '1px solid #e0e0e0', background: '#fff' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                Pages ({effectiveSheetIndex.length})
              </Typography>
              <IconButton size="small" onClick={handleClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
            {!readOnly && (
              <div style={{ display: 'flex', gap: 6, fontSize: 11, marginTop: 6 }}>
                <span
                  style={{ color: '#1565c0', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => {
                    const sched = new Set()
                    effectiveSheetIndex.forEach(s => { if (s.hasSchedule) sched.add(s.page) })
                    setLocalSelected(sched)
                  }}
                >
                  Schedules
                </span>
                <span style={{ color: '#666' }}>|</span>
                <span
                  style={{ color: '#666', cursor: 'pointer' }}
                  onClick={() => setLocalSelected(new Set(effectiveSheetIndex.map(s => s.page)))}
                >
                  All
                </span>
                <span style={{ color: '#666' }}>|</span>
                <span
                  style={{ color: '#666', cursor: 'pointer' }}
                  onClick={() => setLocalSelected(new Set())}
                >
                  None
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                  {localSelected.size} sel
                </span>
              </div>
            )}
          </div>

          {/* Page list */}
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {effectiveSheetIndex.map((sheet) => {
              const cfg = getDisciplineConfig(sheet.discipline)
              const isActive = sheet.page === activePage
              const isChecked = localSelected.has(sheet.page)

              return (
                <div
                  key={sheet.page}
                  ref={el => { sidebarRefs.current[sheet.page] = el }}
                  onClick={() => setActivePage(sheet.page)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    background: isActive ? '#e3f2fd' : 'transparent',
                    borderRight: isActive ? '3px solid #1976d2' : '3px solid transparent',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                >
                  {!readOnly && (
                    <Checkbox
                      checked={isChecked}
                      size="small"
                      color="primary"
                      style={{ padding: 2 }}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => togglePage(sheet.page)}
                    />
                  )}
                  <span style={{
                    color: '#9e9e9e',
                    fontFamily: 'monospace',
                    fontSize: 11,
                    width: 22,
                    textAlign: 'right'
                  }}>
                    {sheet.page}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 12,
                      fontWeight: isActive ? 600 : 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {sheet.sheetNumber ? `${sheet.sheetNumber} ` : ''}{sheet.title}
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 2, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        label={cfg.label}
                        style={{
                          backgroundColor: cfg.bg,
                          color: cfg.color,
                          height: 16,
                          fontSize: 9,
                          fontWeight: 500
                        }}
                      />
                      {sheet.hasSchedule && (
                        <Chip
                          size="small"
                          label="Schedule"
                          style={{
                            backgroundColor: '#fff8e1',
                            color: '#f57f17',
                            height: 16,
                            fontSize: 9,
                            fontWeight: 600
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Sidebar footer */}
          <div style={{ padding: '10px 14px', borderTop: '1px solid #e0e0e0', background: '#fff' }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleClose}
              style={{ textTransform: 'none', fontWeight: 600 }}
            >
              {readOnly ? 'Close' : 'Done Reviewing'}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}

export default VisualReviewModal
